// src/lib/time/today.ts
import { type IsoDate, isIsoDate } from "@/lib/time/dates";
import { formatHours } from "@/lib/time/rules";

/** Vinyl's header chip turns green at a full day (TE-37). */
const FULL_DAY_HOURS = 8;

/** Every entry logged on `date` (PTO included), rounded to the hundredth. */
export function hoursLoggedOn(entries: { date: IsoDate; hours: number }[], date: IsoDate): number {
  const total = entries.filter((e) => e.date === date).reduce((sum, e) => sum + e.hours, 0);
  return Math.round(total * 100) / 100;
}

/**
 * The chip has no aria-label: its accessible name is the text on screen plus a screen-reader-only
 * suffix, so the name always starts with what a person (or a computer-use agent) sees, at every
 * width (rule 12, WCAG 2.5.3): "Today: 6.5 hrs logged" from xl up, "6.5h logged today" below.
 */
export interface TodayChipCopy {
  /** From xl up: "Today: 6.5 hrs". */
  text: string;
  /** Screen readers only, after `text`. */
  textSuffix: string;
  /** Below xl, where the header row is tight: "6.5h". */
  short: string;
  /** Screen readers only, after `short`. */
  shortSuffix: string;
  /** A full day logged. */
  full: boolean;
}

/** The header chip's words; null at 0 hrs, so it is never an alert that reads 0 (rule 7). */
export function todayChip(hours: number): TodayChipCopy | null {
  if (hours <= 0) return null;
  const text = formatHours(hours);
  const amount = `${text} ${text === "1" ? "hr" : "hrs"}`;
  return {
    text: `Today: ${amount}`,
    textSuffix: " logged",
    short: `${text}h`,
    shortSuffix: " logged today",
    full: hours >= FULL_DAY_HOURS,
  };
}

/** The Time page's `?day=` search param: a real "YYYY-MM-DD", otherwise nothing. */
export function parseDayParam(value: unknown): IsoDate | undefined {
  return typeof value === "string" && isIsoDate(value) ? value : undefined;
}
